import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const VariableExpensesGrid = ({ data, onUpdate, onDelete, searchQuery }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const categoryOptions = [
    { value: 'operational', label: 'Operational' },
    { value: 'travel', label: 'Travel & Transport' },
    { value: 'communication', label: 'Communication' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'other', label: 'Other' },
  ];

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery?.toLowerCase();
    return data?.filter(item =>
      item?.name?.toLowerCase()?.includes(query) ||
      item?.category?.toLowerCase()?.includes(query)
    );
  }, [data, searchQuery]);

  const handleEdit = (item) => {
    setEditingId(item?.id);
    setEditData({ ...item });
  };

  const handleSave = () => {
    onUpdate(editData);
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows?.length === filteredData?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData?.map(item => item?.id));
    }
  };

  const formatCurrency = (value) => {
    return `OMR ${parseFloat(value || 0)?.toFixed(3)}`;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[800px]">
        <thead>
          <tr className="bg-muted border-b border-border">
            <th className="w-12 px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedRows?.length === filteredData?.length && filteredData?.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-border"
              />
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Expense Name</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Category</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Monthly Cost</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Description</th>
            <th className="w-32 px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData?.map((item) => (
            <tr key={item?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedRows?.includes(item?.id)}
                  onChange={() => handleSelectRow(item?.id)}
                  className="w-4 h-4 rounded border-border"
                />
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Input
                    type="text"
                    value={editData?.name ?? ''}
                    onChange={(e) => setEditData({ ...editData, name: e?.target?.value })}
                    className="w-full"
                  />
                ) : (
                  <span className="text-sm text-foreground font-medium">{item?.name}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Select
                    options={categoryOptions}
                    value={editData?.category}
                    onChange={(value) => setEditData({ ...editData, category: value })}
                  />
                ) : (
                  <span className="text-sm text-muted-foreground capitalize">{item?.category}</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {editingId === item?.id ? (
                  <Input
                    type="number"
                    value={editData?.monthlyCost ?? ''}
                    onChange={(e) => setEditData({ ...editData, monthlyCost: parseFloat(e?.target?.value) || 0 })}
                    className="w-full text-right"
                  />
                ) : (
                  <span className="text-sm text-foreground font-medium">{formatCurrency(item?.monthlyCost)}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editingId === item?.id ? (
                  <Input
                    type="text"
                    value={editData?.description ?? ''}
                    onChange={(e) => setEditData({ ...editData, description: e?.target?.value })}
                    className="w-full"
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">{item?.description || '—'}</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-center gap-2">
                  {editingId === item?.id ? (
                    <>
                      <Button variant="success" size="xs" iconName="Check" onClick={handleSave} />
                      <Button variant="ghost" size="xs" iconName="X" onClick={handleCancel} />
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" size="xs" iconName="Edit" onClick={() => handleEdit(item)} />
                      <Button variant="ghost" size="xs" iconName="Trash2" onClick={() => onDelete(item?.id)} />
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredData?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="FileX" size={48} className="text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">No variable expenses found. Add your first variable expense using the "Add New" button.</p>
        </div>
      )}
    </div>
  );
};

export default VariableExpensesGrid;